<script setup lang="ts">
import { ref, h, computed, watch, onMounted } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import cloneDeep from 'lodash/cloneDeep';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required, minValue } from '@vuelidate/validators';
import { INCENTIVE_GROUP_TYPE, LANG_FRENCH } from '@/constants';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useTourManagementStore } from '@/store/TourManagementStore';
import { useSubmitQuoteStore } from '@/store/SubmitQuoteStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { useTabComposable } from './useTabComposable';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useComponentComposable } from '@/components/useComponentComposable';
import { formatDate } from '@/utils/dateUtils';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import InputTextAera from 'primevue/textarea';
import Fluid from 'primevue/fluid';
import PDFModal from '@/components/modal/PDFModal.vue';
import TourTabInputField from '@/components/tour/TourTabInputField.vue';
import TourTabSelectedAfter from '@/components/tour/TourTabSelectedAfter.vue';
import TourSelectedTable from '@/components/modal/tour/TourSelectedTable.vue';
import ValidateQuoteFormNotification from '@/components/modal/ValidateQuoteFormNotification.vue';
import ValidateOptionModal from './ValidateOptionModal.vue';
import SubmitNotificationModal from '@/components/modal/SubmitNotificationModal.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const props = defineProps<{
  advancedForm: object[];
  isValidation: boolean;
  avgPaxRoomData: number;
  selectedLanguage: string;
  quoteIntro: object;
  calendarData?: object;
}>();

const emits = defineEmits<{
  submit: [];
  clear: [];
}>();

const { getUserQuote, getUserParam, renderOriginQuote, checkTotalNumberOfRooms, checkTotalPassenger, checkTotalAdultAndRoom, checkAdultEachPackages, checkRoomsEachPackage, validateDateTime, handleRedirectFlight, handleSetLimit } = useTabComposable();
const { formatCountryFromCity } = useComponentComposable();
const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const dialog = useDialog();
const adminSetupStore = useAdminSetupStore();
const tourManagementStore = useTourManagementStore();
const submitQuoteStore = useSubmitQuoteStore();
const viewQuoteStore = useViewQuoteStore();
const isWorksheetRequote = computed(() => viewQuoteStore.isWorksheetRequote);
const quote = computed(() => viewQuoteStore.selectQuote);
const mdtOptionConfig = computed(() => adminSetupStore.optionConfig?.mdtOptionConfig);
const isRequote = computed(() => viewQuoteStore.selectIsRequote);
const currentLanguage = getCurrentLocale();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const tourMinimumConfig = computed(() => adminSetupStore.minimumConfig?.tourConfig);
const listGateways = computed(() => {
  return tourManagementStore.selectListGateways;
});
const listSelectedTour = ref([]);
const isShowError = ref(false);
const isLoading = ref(false);
const isDisplayTour = ref(false);
const isClearAgencyInfo = ref(false);
const tourOptionConfigValue = ref(1);
const tourData = ref<any>([
  {
    gateway: '',
    destination: '',
    isDepartureMonth: true,
    departureDay: null,
    departureMonth: '',
    tourDurations: '',
    adults: 0,
    children: 0,
    numOfRooms: 0,
    tourName: [],
    tours: [],
    additionalNote: ''
  }
]);

const handleSubmitForm = async () => {
  await emits('submit');
};

const rules = ref({
  $each: helpers.forEach({
    gateway: { required: helpers.withMessage(t('validate.required'), required) },
    destination: { required: helpers.withMessage(t('validate.required'), required) },
    tourDurations: { required: helpers.withMessage(t('validate.required'), required) },
    adults: {
      required: helpers.withMessage(t('validate.required'), required),
      minValue: helpers.withMessage(t('validate.min_value'), minValue(1))
    },
    numOfRooms: {
      required: helpers.withMessage(t('validate.required'), required),
      minValue: helpers.withMessage(t('validate.min_value'), minValue(1))
    },
    tourName: { required: helpers.withMessage(t('validate.required'), required) }
  })
});

const v$ = useVuelidate(rules.value, tourData.value);

const handleSubmitTour = (tour: any) => {
  listSelectedTour.value = tour;
  isDisplayTour.value = true;
};
const tourStore = ref([]);

const openEditTour = (isEdit: boolean, tourData: any) => {
  if (tourData.length) {
    tourStore.value = tourData;
  }
  const dataEdit = isEdit ? tourStore.value : tourData;
  const dialogRef = dialog.open(TourSelectedTable, {
    props: {
      header: t('multi_day_tour.select_your_tour_tilte'),
      modal: true,
      maximizable: true,
      style: {
        width: '90vw'
      }
    },
    data: {
      tourSearchData: dataEdit,
      selectedTours: listSelectedTour.value
    },
    emits: {
      onApply: (data: any) => {
        handleSubmitTour(data);
        dialogRef.close();
      },
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const removeOptionTour = () => {
  tourData.value.pop();
};

const addOptionTour = () => {
  tourData.value.push({
    gateway: '',
    destination: tourData.value[0].destination,
    isDepartureMonth: tourData.value[0].isDepartureMonth,
    departureDay: tourData.value[0].departureDay,
    departureMonth: tourData.value[0].departureMonth,
    tourDurations: tourData.value[0].tourDurations,
    adults: 0,
    children: 0,
    numOfRooms: 0,
    tourName: tourData.value[0].tourName,
    tours: tourData.value[0].tours,
    brochure: tourData.value[0].brochure,
    additionalNote: ''
  });
};

const resetDataTour = (tour: any) => {
  tour.gateway = '';
  tour.destination = '';
  tour.isDepartureMonth = true;
  tour.departureDay = null;
  tour.departureMonth = '';
  tour.tourDurations = '';
  tour.adults = 0;
  tour.children = 0;
  tour.numOfRooms = 0;
  tour.tourName = [];
  tour.tours = [];
  tour.additionalNote = '';
};

const clearDataTour = () => {
  tourData.value.forEach((tour: any) => {
    resetDataTour(tour);
  });
  if (tourData.value.length > 1) {
    tourData.value.splice(1);
  }
};

const showValidateQuoteFormNotification = (message: string = t('advance_quote.form.min_pax_package', { minPax: tourMinimumConfig.value.minPax, minRoom: tourMinimumConfig.value.minRoomsCabins })) => {
  const dialogValidateQuoteRef = dialog.open(ValidateQuoteFormNotification, {
    data: {
      message: message
    },
    templates: {
      footer: () => {
        return [
          h(
            'div',
            { class: 'text-center' },
            h(Button, {
              label: t('advance_quote.form.return_to_form'),
              autofocus: true,
              onClick: () => dialogValidateQuoteRef.close()
            })
          )
        ];
      }
    },
    props: {
      header: t('advance_quote.form.notice'),
      modal: true,
      style: {
        maxWidth: '90vw'
      }
    }
  });
};

const checkIsDepMonth = (tour: any) => {
  return tour.departureMonth ? tour.departureMonth : tour.departureDay;
};

const checkIsDepDateValue = (tour: any) => {
  if (tour.depDate) {
    return new Date(tour.depDate);
  } else if (tour.departureMonth) {
    return tour?.departureMonth;
  } else {
    return tour?.departureDay;
  }
};

const calculateEndDate = (tour: any) => {
  const tourDuration = tour.tourDurations == -1 ? 0 : +tour.tourDurations;
  const departureDate = checkIsDepDateValue(tour);
  return new Date(DateTime.fromJSDate(departureDate).plus({ days: tourDuration }));
};

const setTourPassengers = (tour: any, tourIndex: any) => {
  return tour.tours.map((value: any) => {
    return {
      ...value,
      adtNo: tourData.value[tourIndex].adults,
      chdNo: tourData.value[tourIndex].children,
      numOfRooms: tourData.value[tourIndex].numOfRooms,
      tourDurationEn: value.titleEn,
      tourDurationFr: value.titleFr,
      tourType: value.type,
      retDate: formatDate(calculateEndDate(value), 'yyyy-LL-dd HH:mm', true).replace(' ', 'T'),
      visitedCitiesEn: value.regionsVisitedEn,
      visitedCitiesFr: value.regionsVisitedFr,
      destinationCode: value.destCode,
      imageLink: value.thumbnail,
      tourDuration: value.tourDurations,
      tourInclusionEn: value.tourInclusionsEn,
      tourInclusionFr: value.tourInclusionsFr,
      tourLinkEn: value.descriptionUrlEn,
      tourLinkFr: value.descriptionUrlFr
    };
  });
};

const getSubmitPayload = () => {
  const payload = [];
  tourData.value.forEach((tour: any, tourIndex: any) => {
    payload.push({
      currency: '',
      rateOfExchange: '',
      gate: tour.gateway.gateNameEn,
      gateFr: tour.gateway.gateNameFr,
      gateCode: tour.gateway.gateCode,
      gateId: tour.gateway.gateId,
      gateReferenceType: tour.gateway.referenceType,
      dest: `${tour.destination.destNameEn} (${tour.destination.destCountryEn})`,
      destFr: `${tour.destination.destNameFr} (${tour.destination.destCountryFr})`,
      destCode: tour.destination.destCode,
      destId: tour.destination.destId,
      destReferenceType: tour.destination.referenceType,
      parentTourId: tour.destination?.parentTourId ? tour.destination?.parentTourId : '',
      parentDestId: tour.destination?.parentDestId ? tour.destination?.parentDestId : '',
      airportCode: tour.destination.destCode,
      isDepartureMonth: tour.isDepartureMonth,
      isDepartureDate: !tour.isDepartureMonth,
      depDate: formatDate(checkIsDepMonth(tour), 'yyyy-LL-dd HH:mm', true).replace(' ', 'T'),
      retDate: formatDate(checkIsDepMonth(tour), 'yyyy-LL-dd HH:mm', true).replace(' ', 'T'),
      duration: tour.tourDurations,
      adtNo: tour.adults,
      chdNo: tour.children,
      numOfRooms: tour.numOfRooms,
      infNo: 0,
      tours: setTourPassengers(tour, tourIndex),
      tourTtsId: tour.tourName.svId,
      tourSvId: tour.tourName.ttsId,
      pnr: '',
      promotions: '',
      comment: '',
      additionalNote: tour.additionalNote,
      brochure: tour.brochure
    });
  });
  return payload;
};

const showModalErrorPackage = (header: string, messageError: string, subMessageError?: string, isTour?: boolean) => {
  const modalRef = dialog.open(ValidateQuoteFormNotification, {
    props: {
      header: header,
      modal: true,
      style: {
        width: '40vw'
      }
    },
    data: {
      message: messageError,
      subMessage: subMessageError,
      isTour: isTour
    },
    templates: {
      footer: () => {
        return [h('div', { class: 'text-center' }, h(Button, { autofocus: true, label: t('common.buttons.close'), onClick: () => modalRef.close() }))];
      }
    }
  });
};

const checkMaxRoomsMDT = async (quoteData: any) => {
  let isValid: boolean = true;
  if (quoteData.gType !== INCENTIVE_GROUP_TYPE) {
    const payload: any[] = [];
    quoteData.multiDayTour.forEach((item: any) => {
      payload.push({
        brochureCode: item.brochure,
        destinationCode: item.destId,
        hotelData: null,
        numberOfRooms: item.numOfRooms
      });
    });
    isValid = await adminSetupStore.checkMaxRooms(payload);
  }
  return isValid;
};

const checkPayloadSubmitQuote = async (tour: any[], isMultipleOptionForSamePassenger?: boolean) => {
  const agent = cloneDeep(isWorksheetRequote.value && !isClearAgencyInfo.value ? getUserQuote(quote.value) : getUserParam());
  const payloadData = {
    ...agent,
    lang: props.selectedLanguage,
    gContact: props.advancedForm[0].groupContact,
    gName: props.advancedForm[0].groupName,
    gTypeFr: props.advancedForm[0].groupTypeFr,
    gType: props.advancedForm[0].groupType,
    qType: 'MDT',
    gTypeId: props.advancedForm[0].groupTypeId,
    multiDayTour: tour,
    advanced: true,
    insta: props.advancedForm[0].instaQuote,
    avgPaxRoom: props.avgPaxRoomData,
    originalQuote: renderOriginQuote(true, quote.value?.sk),
    isMultipleOptionForSamePassenger: isMultipleOptionForSamePassenger,
    isReplicate: isWorksheetRequote.value
  };

  const checkOverDatePackage = tour?.some((item) => validateDateTime(item?.depDate, props.advancedForm[0].groupType));
  if (checkOverDatePackage) {
    showModalErrorPackage(t('common.labels.unable_to_process'), t('notification.over_days'));
  } else {
    const isMaxRoomValid = await checkMaxRoomsMDT(payloadData);
    if (!isMaxRoomValid) {
      showValidateQuoteFormNotification(t('advance_quote.form.max_rooms_invalid'));
    } else {
      submitQuote(payloadData);
    }
  }
};

const openModalErrorPricing = () => {
  showModalErrorPackage(t('multi_day_tour.result_not_available'), t('multi_day_tour.departure_not_return'), t('multi_day_tour.please_update_query'));
};

const handleShowModalAfterSubmit = (pdfData, quoteId) => {
  if (pdfData) {
    dialog.open(PDFModal, {
      props: {
        modal: true,
        header: t('create_quote.pdf_view'),
        maximizable: true,
        breakpoints: {
          '960px': '75vw',
          '640px': '90vw'
        },
        style: {
          width: '60vw'
        },
        data: pdfData
      }
    });
  } else {
    dialog.open(SubmitNotificationModal, {
      props: {
        modal: true,
        data: quoteId,
        header: t('advance_quote.confirm_quote_id')
      }
    });
  }
};

const handleSubmitOption = (event: any) => {
  const tourData = getSubmitPayload();
  const total = checkTotalAdultAndRoom(tourData);
  const isRequote = isWorksheetRequote.value;
  const validateRequoteWorksheetSubmit = (isValidateRequote: boolean) => {
    if (isValidateRequote && (checkAdultEachPackages(tourData, tourMinimumConfig.value?.minPax) || checkRoomsEachPackage(tourData, tourMinimumConfig.value?.minRoomsCabins))) {
      showValidateQuoteFormNotification();
    } else {
      checkPayloadSubmitQuote(tourData, true);
    }
  };

  const checkOptionNotMultipleForSamePassenger = () => {
    if (!isRequote && (checkTotalPassenger(tourData, tourMinimumConfig.value?.minPax) || checkTotalNumberOfRooms(tourData, tourMinimumConfig.value?.minRoomsCabins))) {
      showValidateQuoteFormNotification();
    } else if (!isRequote) {
      const totalPassenger = total[0].adtNo + total[0].chdNo;
      showConfirmQuoteCriteria(tourData, totalPassenger, total[0].numOfRooms, false);
    } else {
      checkPayloadSubmitQuote(tourData, false);
    }
  };
  if (event) {
    validateRequoteWorksheetSubmit(!isRequote);
  } else {
    checkOptionNotMultipleForSamePassenger();
  }
};

const showConfirmQuoteCriteria = (tours: any[], passengers?: number, rooms?: number, isMultipleOptionForSamePassenger?: boolean) => {
  const dialogRef = dialog.open(ValidateQuoteFormNotification, {
    data: {
      message: `${t('advance_quote.confirm_criteria')} ${passengers} ${t('advance_quote.passengers')} and ${rooms} ${t('common.rooms')}`
    },
    templates: {
      footer: () => {
        return [
          h(
            'div',
            { class: 'text-center' },
            h(Button, {
              label: t('advance_quote.form.submit'),
              autofocus: true,
              onClick: () => {
                checkPayloadSubmitQuote(tours, isMultipleOptionForSamePassenger);
                dialogRef.close();
              }
            })
          )
        ];
      }
    },
    props: {
      header: t('advance_quote.form.notice'),
      modal: true
    }
  });
};

const showValidateOptionModal = () => {
  const dialogValidationRef = dialog.open(ValidateOptionModal, {
    props: {
      header: t('create_quote.confirm'),
      modal: true,
      style: {
        width: '28vw'
      }
    },
    emits: {
      isSave: (event: any) => {
        handleSubmitOption(event);
        dialogValidationRef.close();
      }
    }
  });
};

const submitQuote = (payload: any) => {
  isLoading.value = true;
  submitQuoteStore
    .handleSubmit(payload, undefined, openModalTimeOut, openModalErrorPricing)
    .then((res) => {
      if (res.data) {
        handleShowModalAfterSubmit(res.data?.pdf, res.data?.sk);
      }
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .finally(() => {
      clearAllDataTour();
      isLoading.value = false;
    });
};

const handleSubmitTourForm = async () => {
  await emits('submit');
  const payloadSubmit = getSubmitPayload();
  v$.value.$validate();
  isShowError.value = true;
  if (!v$.value.$error && props.isValidation) {
    if (tourData.value.length > 1) {
      showValidateOptionModal();
    }
    if ((checkTotalPassenger(payloadSubmit, tourMinimumConfig.value?.minPax) || checkTotalNumberOfRooms(payloadSubmit, tourMinimumConfig.value?.minRoomsCabins)) && !isWorksheetRequote.value && tourData.value.length <= 1) {
      showValidateQuoteFormNotification();
      return;
    }
    if (tourData.value.length <= 1) {
      checkPayloadSubmitQuote(payloadSubmit);
      isShowError.value = false;
    }
  }
};

const openModalTimeOut = () => {
  clearAllDataTour();
  showModalErrorPackage(t('advance_quote.form.notice'), t('notification.thank_for_your_patience'), t('notification.timeout_message_notice'), true);
};

const clearAllDataTour = () => {
  clearDataTour();
  emits('clear');
  viewQuoteStore.updateViewQuote({});
  viewQuoteStore.getActionRequoteFromWorksheet(true);
  isClearAgencyInfo.value = true;
  isDisplayTour.value = false;
  listSelectedTour.value = [];
  isShowError.value = false;
};

const clearTourSelect = (getIndex: number, tourKey: string) => {
  if (getIndex == 0) {
    tourData.value.forEach((tour, index) => {
      if (index) {
        tour[tourKey] = '';
      }
    });
  }
  isDisplayTour.value = false;
  listSelectedTour.value = [];
};

const renderQuoteTabIntroHtml = () => {
  if (currentLanguage === LANG_FRENCH) {
    return h('div', { class: 'french-term', innerHTML: props.quoteIntro.termsFr });
  } else {
    return h('div', { class: 'english-term', innerHTML: props.quoteIntro.terms });
  }
};

const getGatewaySelectedQuote = (gateCode: string) => {
  const gatewayFind = listGateways.value.filter((gate: any) => {
    return gate.gateCode == gateCode;
  });
  return gatewayFind[0];
};

const getDestinationsByGateCode = async (gateCode: string, destCode?: string | null, destId?: string | null, parentTourId?: string | null) => {
  const searchData = {
    destCode: destCode,
    destId: destId,
    parentTourId: parentTourId
  };
  isLoading.value = true;
  let dest = '';
  await tourManagementStore
    .getTourCombinationDestByGateCode(gateCode)
    .then((res) => {
      dest = findDestination(searchData, formatCountryFromCity(res.data));
      tourManagementStore.updateTourCombinationDestination(formatCountryFromCity(res.data));
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
  return dest;
};

const findDestination = (searchData: any, listDestination: any) => {
  const selectedDestination = listDestination.find(
    (dest: any) => (searchData.destCode ? dest.destCode === searchData.destCode : true) && (searchData.destId ? dest.destId === searchData.destId : true) && (searchData.parentTourId ? dest.parentTourId === searchData.parentTourId : true)
  );
  return selectedDestination;
};

const getRequoteData = async () => {
  if (quote.value.multiDayTour?.length) {
    if (isRequote.value) {
      tourData.value[0].gateway = await getGatewaySelectedQuote(quote.value.multiDayTour[0].gateCode);
      tourData.value[0].adults = quote.value.multiDayTour[0].adtNo;
      tourData.value[0].children = quote.value.multiDayTour[0].chdNo;
      tourData.value[0].numOfRooms = quote.value.multiDayTour[0].numOfRooms;
      tourData.value[0].destination = await getDestinationsByGateCode(tourData.value[0].gateway.gateCode, quote.value.multiDayTour[0].destCode, quote.value.multiDayTour[0].destId, quote.value.multiDayTour[0].parentTourId);
      tourData.value[0].additionalNote = quote.value.multiDayTour[0].additionalNote;
      tourData.value[0].brochure = quote.value.multiDayTour[0].brochure;
    }
  }
};

const handleConfigReplicateQuote = () => {
  const numberOption = quote.value?.multiDayTour?.length;
  if (numberOption > tourOptionConfigValue.value) {
    tourOptionConfigValue.value = numberOption;
  }
};

const clearTourOptions = () => {
  tourData.value.splice(1);
  if (isRequote.value && isWorksheetRequote.value) {
    handleConfigReplicateQuote();
  }
};

const handleChangeTourGroupType = (advancedForm: any, config: any) => {
  if (advancedForm[0].groupType) {
    tourOptionConfigValue.value = handleSetLimit(advancedForm, config).maxOption;
    clearTourOptions();
  }
};

onMounted(() => {
  getRequoteData();
});

watch(
  () => tourData.value[0],
  (newVal) => {
    tourData.value.forEach((tour, index) => {
      if (index) {
        tour.destination = newVal.destination;
        tour.isDepartureMonth = newVal.isDepartureMonth;
        tour.departureDay = newVal.departureDay;
        tour.departureMonth = newVal.departureMonth;
        tour.tourDurations = newVal.tourDurations;
        tour.tourName = newVal.tourName;
        tour.tours = newVal.tours;
      }
    });
  },
  { deep: true }
);

watch(
  () => props.advancedForm[0].groupTypeId,
  (value) => {
    if (value) {
      tourData.value.forEach((tour) => {
        tour.tourName = [];
      });
      tourData.value.splice(1);
      isDisplayTour.value = false;
    }
  }
);

watch(
  () => props.advancedForm,
  (newValue) => {
    if (newValue) {
      handleChangeTourGroupType(newValue, mdtOptionConfig.value);
    }
  },
  { deep: true, immediate: true }
);
</script>
<template>
  <div class="multi-tour-tab">
    <LoadingSpinner v-if="isLoading" />
    <div class="mb-3 quote-title" v-if="quoteIntro">
      <renderQuoteTabIntroHtml></renderQuoteTabIntroHtml>
    </div>
    <div class="grid mt-3 flex flex-column" v-for="(option, index) in tourData" :key="index">
      <div class="col">
        <p>{{ $t('common.option') }} {{ index + 1 }}</p>
        <p @click="handleRedirectFlight()" v-if="index === 0" class="cursor-pointer flex gap-2 align-items-center justify-content-end"><i class="text-3xl pi pi-exclamation-circle exclamation-icon"></i> {{ $t('common.where_we_fly') }}</p>
        <TourTabInputField
          @handleSubmit="handleSubmitForm()"
          :tourForm="option"
          :advancedForm="advancedForm"
          :calendarData="calendarData"
          :listGateway="listGateways"
          :isValidation="isValidation"
          :tourIndex="index"
          v-model:isLoading="isLoading"
          v-model:isShowError="isShowError"
          :firstOption="tourData[0]"
          v-model:isDisplayTour="isDisplayTour"
          @clearTourBefore="() => clearAllDataTour()"
          @clearTours="(getIndex, tourKey) => clearTourSelect(getIndex, tourKey)"
          @openAutoSearch="(isEdit, tourData) => openEditTour(isEdit, tourData)"
        ></TourTabInputField>
        <TourTabSelectedAfter v-if="isDisplayTour" :tourIndex="index" @openEditTour="(isEdit, tourData) => openEditTour(isEdit, tourData)" :tourForm="option" :listSelectedTour="listSelectedTour"></TourTabSelectedAfter>
      </div>
      <Fluid v-if="authorizations?.advance_quote?.additional_note_view">
        <div class="mt-3 mb-3 flex-col additional-note relative">
          <label for="additionalNote" class="mb-2">{{ $t('advance_quote.form.additional_note') }}</label>
          <InputTextAera inputId="additionalNote" class="not-resize" :maxlength="500" v-model="option.additionalNote" :autoResize="true" rows="5" cols="30" />
          <span class="absolute"> {{ option?.additionalNote?.length }} / 500 </span>
        </div>
      </Fluid>
      <div class="grid mt-5" v-if="(isDisplayTour || index !== 0) && index === tourData.length - 1">
        <div id="add-remove-tour" class="flex w-full justify-content-around mb-3">
          <div href="javascript:" class="block text-primary cursor-pointer remove-option-class" @click="removeOptionTour()" v-if="tourData.length > 1">- {{ $t('advance_quote.form.remove_option') }}</div>
          <div href="javascript:" class="block text-primary cursor-pointer add-option-class" v-if="tourData.length < tourOptionConfigValue" @click="addOptionTour()">+ {{ $t('advance_quote.form.add_option') }}</div>
        </div>
      </div>
    </div>
    <div class="seperator mb-3 mt-5"></div>
    <div>
      <div class="flex gap-3">
        <Button id="submit-btn" :label="$t('advance_quote.form.submit')" :disabled="!isDisplayTour" class="button-submit mt-2 bg-primary uppercase submit-form-class" @click="handleSubmitTourForm()"></Button>
        <Button id="clear-btn" :label="$t('common.buttons.clear')" class="button-submit mt-2 bg-primary uppercase clear-form-class" @click="clearAllDataTour()"></Button>
      </div>
    </div>
  </div>
</template>
